import { Button } from '@/components/common/button'
import { ExternalLinkIcon } from '@/components/icons/externalLink'
import { OPPORTUNITY_TAB_VALUES } from '@/components/opportunity/opportunityOverview/opportunityContent'
import { OPPORTUNITY_PAGE_KEYS } from '@/constants/state'
import { useAnimatedValue } from '@/hooks/useAnimatedValue'
import { useReturnsQuery } from '@/queries/returns.query'
import type { Vault } from '@/types/vault'
import { animated } from '@react-spring/web'
import { formatPreciseAmount, formatPreciseTokenValue } from '@vaultsfyi/common/utils'
import { Link } from 'react-router'
import type { Address } from 'viem'
import { formatUnits } from 'viem'

interface PositionDetailsProps {
  totalPositionValue: bigint
  vault: Vault
  walletAddress: Address | undefined
  onNavigate?: () => void
}

export const PositionDetails = ({ totalPositionValue, vault, walletAddress, onNavigate }: PositionDetailsProps) => {
  const { asset } = vault
  const animatedPositionValue = useAnimatedValue(Number(formatUnits(totalPositionValue, asset.decimals)))
  const { returnsNative, isLoading: isReturnsLoading } = useReturnsQuery({ address: walletAddress, vault })

  return (
    <div className="flex flex-col gap-4 rounded-2xl border bg-surface p-4">
      <div>
        <h5 className="font-medium text-base text-heading-foreground">Your active position</h5>
        <p className="mt-2 text-sm">A snapshot of your current investment — what you put in and what it's worth now.</p>
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:flex-wrap">
        <div className="flex flex-1 flex-col gap-1 rounded-2xl border bg-page p-3">
          <h6 className="text-sm">Deposited</h6>
          {totalPositionValue === 0n ? (
            <p className="font-medium text-2xl text-heading-foreground">––</p>
          ) : (
            <p className="whitespace-nowrap">
              <animated.span className="font-medium text-2xl text-heading-foreground">
                {animatedPositionValue.to((amount) => {
                  return formatPreciseAmount({ amount })
                })}
              </animated.span>{' '}
              <span className="text-lg">{asset.symbol}</span>
            </p>
          )}
        </div>

        <div className="flex flex-1 flex-col gap-1 rounded-2xl border bg-page p-3">
          <h6 className="text-sm">Total returns</h6>
          {isReturnsLoading ? (
            <p className="font-medium text-2xl text-heading-foreground">
              <span className="animate-pulse duration-700">–</span>
              <span className="animate-pulse delay-300 duration-700">–</span>
            </p>
          ) : (
            <p className="whitespace-nowrap">
              {returnsNative !== undefined ? (
                <>
                  <span className="font-medium text-2xl text-heading-foreground">
                    {formatPreciseTokenValue({ value: returnsNative, decimals: vault.asset.decimals })}
                  </span>{' '}
                  <span className="text-lg">{asset.symbol}</span>
                </>
              ) : (
                <span className="font-medium text-2xl text-heading-foreground">––</span>
              )}
            </p>
          )}
        </div>
      </div>
      <Button
        variant="default"
        size="default"
        className="ml-auto flex items-center gap-1 rounded-full font-medium text-accent"
        onClick={onNavigate}
        asChild
      >
        <Link to={`?${OPPORTUNITY_PAGE_KEYS.tab.key}=${OPPORTUNITY_TAB_VALUES.position}`}>
          View position & activity
          <ExternalLinkIcon size={16} />
        </Link>
      </Button>
    </div>
  )
}
